import {
  Button,
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
} from '@chakra-ui/react';
import PropsTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import slPaymentService from '../../../../../../service/slPaymentService';
import { notify } from '../../../../../../service/pnotifyService';
import { SECTION_STATUS } from '../../../../constants';
import { useAppContext } from '../../../../context/appContext';
import Loading from '../Loading';
import ErrorPanel from '../ErrorPanel';

const DeleteCreditCardModal = ({ isOpen, onCancel, onComplete, item = {} }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(SECTION_STATUS.SUCCESS);
  const { slFeatureService } = useAppContext();
  const { expired, brandImg, id, last } = item;

  const handleSubmit = useCallback(async () => {
    if (status !== SECTION_STATUS.SUCCESS) {
      return;
    }
    try {
      setStatus(SECTION_STATUS.LOADING);
      await slPaymentService.deletePaymentInstruments(id);
      setStatus(SECTION_STATUS.SUCCESS);
      notify(t('user.delete_credit_card.success'), {});
      onComplete();
    } catch (e) {
      setStatus(SECTION_STATUS.FAILURE);
      notify(t('user.delete_credit_card.fail'), {
        customClass: 'error',
        icon: 'fa fa-exclamation-triangle',
      });
    }
  }, [id, onComplete, status, t]);

  return (
    <Modal
      isOpen={isOpen}
      onOverlayClick={onCancel}
      onClose={onCancel}
      isCentered
      size="4xl"
    >
      <ModalOverlay />
      <ModalContent className="credit-card-delete-modal__content">
        <ModalHeader>
          <Text
            variant="sectionTitle"
            className="member-center-profile__section-title-text"
            fontSize="16px"
          >
            {t('user.delete_credit_card')}
          </Text>
        </ModalHeader>
        <ModalBody>
          {status === SECTION_STATUS.FAILURE ? (
            <ErrorPanel onReload={() => setStatus(SECTION_STATUS.SUCCESS)} />
          ) : (
            <>
              {status === SECTION_STATUS.LOADING && (
                <Box mb="10px" mt="10px">
                  <Loading />
                </Box>
              )}
              <Flex className="credit-card-delete-modal__item">
                <img src={brandImg} width="40px" />
                <Box flex="1" paddingLeft="10px">
                  <Text variant={expired ? 'avatarDisableText' : 'avatarText'}>
                    ****{last}
                  </Text>
                  {expired && (
                    <Text variant="expiredTag" paddingLeft="10px">
                      {t('user.credit_card.expired')}
                    </Text>
                  )}
                </Box>
              </Flex>
              <Text variant="avatarText">
                {slFeatureService.hasFeature('subscription_product')
                  ? t(
                      'user.delete_credit_card.description.with_subscription_product',
                    )
                  : t('user.delete_credit_card.description')}
              </Text>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {status !== SECTION_STATUS.FAILURE && (
            <Flex justifyContent="flex-end">
              <Button
                variant="outline"
                className="member-center-profile__outline-button"
                onClick={onCancel}
                mr="10px"
              >
                {t('user.delete_credit_card.cancel')}
              </Button>
              <Button variant="fillSuccess" onClick={handleSubmit}>
                {t('user.delete_credit_card.delete')}
              </Button>
            </Flex>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

DeleteCreditCardModal.propTypes = {
  isOpen: PropsTypes.bool,
  onCancel: PropsTypes.func.isRequired,
  onComplete: PropsTypes.func.isRequired,
  item: PropsTypes.shape({
    id: PropsTypes.string.isRequired,
    brandImg: PropsTypes.string.isRequired,
    last: PropsTypes.string.isRequired,
    expired: PropsTypes.bool,
  }),
};

export default DeleteCreditCardModal;
